import { useCallback, useEffect, useMemo } from "react"
import { SHIFT_TYPES_LIMIT } from "../constants"
import { StaffSchedule } from "../interfaces"
import { Period, buildScheduleListArgs } from "../model/period"
import {
  countsByShiftType,
  grandTotal,
  totalsByDay,
  totalsByStaff,
} from "../model/scheduleCells"
import {
  useSchedulesListQuery,
  useShiftTypesQuery,
  useStaffListQuery,
} from "../store"

const EMPTY_MAP: StaffSchedule.ScheduleMap = {}
const EMPTY_STAFF: StaffSchedule.Staff[] = []

/**
 * Владеет тремя запросами экрана и всеми производными от них.
 *
 * `refetchSchedules` перезапрашивает ТОЛЬКО расписание — его зовут откат ошибки мазка
 * и кнопка «Обновить»; справочник и сотрудники при этом не нужны.
 * `refetchAll` — все три (кнопка «Повторить» на экране ошибки, спека раздел 12).
 */
export const useScheduleData = (period: Period) => {
  const shiftTypesQuery = useShiftTypesQuery()
  const staffQuery = useStaffListQuery()
  // Аргументы обязаны быть тождественны тем, что использует usePaintCommit в
  // updateQueryData: buildScheduleListArgs — единственный их источник (спека, раздел 6).
  const schedulesQuery = useSchedulesListQuery(buildScheduleListArgs(period))

  // limit=200 — максимум по контракту. Если справочник его превысит, легенда окажется
  // урезанной и реальные ячейки начнут выглядеть как «тип удалён». Пусть это выясняется
  // из консоли, а не из жалобы пользователя.
  const shiftTypes = shiftTypesQuery.data
  useEffect(() => {
    if (shiftTypes?.length === SHIFT_TYPES_LIMIT) {
      console.warn(
        `staffSchedule: справочник типов смен вернул ровно ${SHIFT_TYPES_LIMIT} записей — легенда может быть урезана`,
      )
    }
  }, [shiftTypes])

  // Легенда в Map: мягко удалённый тип исчезает из справочника, но остаётся в записях —
  // ячейка с неизвестным id рисуется серой с подписью «Тип смены удалён».
  const legend = useMemo(
    () => new Map((shiftTypes ?? []).map((t) => [t.id, t])),
    [shiftTypes],
  )

  const cells = schedulesQuery.data ?? EMPTY_MAP

  // Агрегаты считаем по видимому периоду (карта содержит ровно его) — бэкенд их не отдаёт.
  const aggregates = useMemo(
    () => ({
      totalsByStaff: totalsByStaff(cells),
      totalsByDay: totalsByDay(cells),
      grandTotal: grandTotal(cells),
      countsByShiftType: countsByShiftType(cells),
    }),
    [cells],
  )

  const { refetch: refetchShiftTypes } = shiftTypesQuery
  const { refetch: refetchStaff } = staffQuery
  const { refetch: refetchSchedules } = schedulesQuery
  const refetchAll = useCallback(() => {
    refetchShiftTypes()
    refetchStaff()
    refetchSchedules()
  }, [refetchShiftTypes, refetchStaff, refetchSchedules])

  return {
    cells,
    staff: staffQuery.data ?? EMPTY_STAFF,
    legend,
    ...aggregates,
    isLoading:
      shiftTypesQuery.isLoading ||
      staffQuery.isLoading ||
      schedulesQuery.isLoading,
    isFetching: schedulesQuery.isFetching,
    error: shiftTypesQuery.error ?? staffQuery.error ?? schedulesQuery.error,
    refetchSchedules,
    refetchAll,
  }
}
